import { SubscriptionCardKind, SubscriptionStatus } from '@griply/utils';
import { format } from 'date-fns';
import { useSubscriptionCards, useUserHasPremium } from 'features/user';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRightStraight, Logo } from 'shared/assets/icons';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { Icon } from 'shared/components/ui/icon';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useRedirectToStripeUrlOrStartTrial } from 'shared/hooks/use-redirect-to-stripe-url-or-start-trial';
import { useSubscriptionPrices } from 'shared/hooks/use-subscription-prices';
import { useUser } from 'shared/hooks/use-user';
import { PriceInterval } from 'shared/types/price';

import * as Styled from './settings-form.style';
import { SubscriptionManagementLinks } from './subscription-management-links';

export const PaymentManagement: React.FC = () => {
  const user = useUser();
  const { prices } = useSubscriptionPrices();
  const { t } = useTranslation();
  const userHasPremium = useUserHasPremium();
  const openPremiumDialog = useOpenPremiumDialog();
  const {
    redirectOrStartTrial: redirectToStripeUrl,
    isLoading: lifetimePriceLoading,
  } = useRedirectToStripeUrlOrStartTrial();

  const cards = useSubscriptionCards();

  // lifetime
  const lifetimePrice = useMemo(
    () => prices.find(({ interval }) => interval === PriceInterval.Lifetime),
    [prices],
  );

  const activeLifetime = cards.find(
    ({ kind }) => kind === SubscriptionCardKind.Lifetime,
  );

  const lifeTimeIsTrial =
    activeLifetime?.status === SubscriptionStatus.Trial ||
    activeLifetime?.status === SubscriptionStatus.TrialExpired;
  const lifeTimePurchased =
    activeLifetime?.status === SubscriptionStatus.Purchased;
  const lifeTimeTrialExpired =
    activeLifetime?.status === SubscriptionStatus.TrialExpired;

  const showLifetimePrice =
    !!lifetimePrice && !activeLifetime && !!user?.subscriptions?.length;

  const onPurchaseLifetime = () => {
    if (!lifetimePrice) {
      return;
    }

    redirectToStripeUrl({
      interval: lifetimePrice.interval,
      coupon: lifetimePrice.coupon?.id,
    });
  };

  // promotional
  const promotionalSubscription = cards.find(
    ({ kind }) => kind === SubscriptionCardKind.Promotional,
  );

  // subscriptions
  const activeSubscription = cards.find(
    ({ kind }) => kind === SubscriptionCardKind.Subscription,
  );

  const activeSubscriptionIsApple = !!activeSubscription?.isApple;
  const activeSubscriptionIsTrial =
    activeSubscription?.status === SubscriptionStatus.Trial;
  const activeSubscriptionPurchased =
    activeSubscription?.status === SubscriptionStatus.Active;
  const activeSubscriptionCanceled =
    activeSubscription?.status === SubscriptionStatus.Canceled;

  return (
    <>
      {(!!activeSubscription || !!activeLifetime || !userHasPremium) && (
        <Styled.Section>
          {!userHasPremium && (
            <Styled.SplitSectionRow>
              <Styled.SubscriptionContainer>
                <Styled.SubscriptionHeader>
                  <Styled.SubscriptionHeaderContent>
                    <Styled.SubscriptionIcon>
                      <Icon icon={Logo} />
                    </Styled.SubscriptionIcon>

                    <Styled.SubscriptionHeaderTitle>
                      {t(
                        'forms.user-settings.sections.subscriptions.restart.label',
                      )}
                    </Styled.SubscriptionHeaderTitle>
                  </Styled.SubscriptionHeaderContent>

                  <Button
                    onClick={openPremiumDialog}
                    size={Sizes.Small}
                    variant={Variants.Contained}
                    color={Colors.Primary}
                    endIcon={ArrowUpRightStraight}
                  >
                    {t(
                      'forms.user-settings.sections.subscriptions.restart.button.label',
                    )}
                  </Button>
                </Styled.SubscriptionHeader>

                <Styled.SubscriptionDescription>
                  {t(
                    'forms.user-settings.sections.subscriptions.restart.description',
                  )}
                </Styled.SubscriptionDescription>
              </Styled.SubscriptionContainer>
            </Styled.SplitSectionRow>
          )}

          {!!activeLifetime && (
            <Styled.SplitSectionRow>
              <Styled.SubscriptionContainer>
                <Styled.SubscriptionHeader>
                  <Styled.SubscriptionHeaderContent>
                    <Styled.SubscriptionIcon>
                      <Icon icon={Logo} />
                    </Styled.SubscriptionIcon>

                    <Styled.SubscriptionHeaderTitle>
                      {t(
                        'forms.user-settings.sections.subscriptions.premium.lifetime.label',
                      )}

                      {(lifeTimePurchased ||
                        lifeTimeIsTrial ||
                        lifeTimeTrialExpired) && (
                        <Styled.Pill
                          $success={lifeTimePurchased}
                          $warning={lifeTimeIsTrial}
                          $error={lifeTimeTrialExpired}
                        >
                          {t(
                            lifeTimeTrialExpired
                              ? 'forms.user-settings.sections.subscriptions.premium.lifetime.pill.trial-expired'
                              : lifeTimeIsTrial
                                ? 'forms.user-settings.sections.subscriptions.premium.lifetime.pill.trial'
                                : 'forms.user-settings.sections.subscriptions.premium.lifetime.pill.purchased',
                          )}
                        </Styled.Pill>
                      )}
                    </Styled.SubscriptionHeaderTitle>
                  </Styled.SubscriptionHeaderContent>

                  {!lifeTimePurchased && !!lifetimePrice && (
                    <Button
                      onClick={onPurchaseLifetime}
                      size={Sizes.Small}
                      variant={Variants.Outlined}
                      color={Colors.Inherit}
                      disabled={
                        lifetimePriceLoading || activeSubscriptionIsApple
                      }
                      endIcon={ArrowUpRightStraight}
                    >
                      {t(
                        'forms.user-settings.sections.subscriptions.premium.lifetime.button.label',
                      )}
                      {lifetimePriceLoading && (
                        <Styled.ButtonLoader color="primary" size={12} />
                      )}
                    </Button>
                  )}
                </Styled.SubscriptionHeader>

                <Styled.SubscriptionDescription>
                  {t(
                    lifeTimeTrialExpired
                      ? 'forms.user-settings.sections.subscriptions.premium.lifetime.trial-expired.description'
                      : lifeTimeIsTrial
                        ? 'forms.user-settings.sections.subscriptions.premium.lifetime.trial.description'
                        : 'forms.user-settings.sections.subscriptions.premium.lifetime.purchased.description',
                    {
                      date:
                        lifeTimeIsTrial || lifeTimeTrialExpired
                          ? activeLifetime.expiresAt
                            ? format(activeLifetime.expiresAt, 'd MMM yyyy')
                            : undefined
                          : format(activeLifetime.purchasedAt, 'd MMM yyyy'),
                    },
                  )}
                </Styled.SubscriptionDescription>
              </Styled.SubscriptionContainer>
            </Styled.SplitSectionRow>
          )}

          {!!activeSubscription && (
            <Styled.SplitSectionRow>
              <Styled.SubscriptionContainer>
                <Styled.SubscriptionHeader>
                  <Styled.SubscriptionHeaderContent>
                    <Styled.SubscriptionIcon>
                      <Icon icon={Logo} />
                    </Styled.SubscriptionIcon>

                    <Styled.SubscriptionHeaderTitle>
                      {t(
                        'forms.user-settings.sections.subscriptions.premium.subscription.label',
                      )}

                      {(activeSubscriptionPurchased ||
                        activeSubscriptionIsTrial ||
                        activeSubscriptionCanceled) && (
                        <Styled.Pill
                          $success={activeSubscriptionPurchased}
                          $warning={activeSubscriptionIsTrial}
                          $error={activeSubscriptionCanceled}
                        >
                          {t(
                            activeSubscriptionCanceled
                              ? 'forms.user-settings.sections.subscriptions.premium.pill.canceled'
                              : activeSubscriptionIsTrial
                                ? 'forms.user-settings.sections.subscriptions.premium.pill.trial'
                                : 'forms.user-settings.sections.subscriptions.premium.pill.active',
                          )}
                        </Styled.Pill>
                      )}
                    </Styled.SubscriptionHeaderTitle>
                  </Styled.SubscriptionHeaderContent>

                  {!!(
                    activeSubscription.managementUrl ||
                    activeSubscription.stripeCustomerId
                  ) && (
                    <SubscriptionManagementLinks
                      managementUrl={activeSubscription.managementUrl}
                      stripeCustomerId={activeSubscription.stripeCustomerId}
                      isApple={activeSubscriptionIsApple}
                    />
                  )}
                </Styled.SubscriptionHeader>

                <Styled.SubscriptionDescription>
                  {t(
                    activeSubscriptionCanceled
                      ? 'forms.user-settings.sections.subscriptions.premium.expires.description'
                      : activeSubscriptionIsTrial
                        ? 'forms.user-settings.sections.subscriptions.premium.trial.description'
                        : 'forms.user-settings.sections.subscriptions.premium.description',
                    {
                      date: activeSubscription.expiresAt
                        ? format(activeSubscription.expiresAt, 'd MMM yyyy')
                        : undefined,
                    },
                  )}
                </Styled.SubscriptionDescription>
              </Styled.SubscriptionContainer>
            </Styled.SplitSectionRow>
          )}

          {showLifetimePrice && !!lifetimePrice && (
            <Styled.SplitSectionRow>
              <Styled.SubscriptionContainer>
                <Styled.SubscriptionHeader>
                  <Styled.SubscriptionHeaderContent>
                    <Styled.SubscriptionIcon>
                      <Icon icon={Logo} />
                    </Styled.SubscriptionIcon>

                    <Styled.SubscriptionHeaderTitle>
                      {t(
                        'forms.user-settings.sections.subscriptions.premium.lifetime.label',
                      )}
                    </Styled.SubscriptionHeaderTitle>
                  </Styled.SubscriptionHeaderContent>
                  <Button
                    onClick={onPurchaseLifetime}
                    size={Sizes.Small}
                    variant={Variants.Contained}
                    color={Colors.TextPrimary}
                    disabled={lifetimePriceLoading}
                    endIcon={ArrowUpRightStraight}
                  >
                    {t(
                      'forms.user-settings.sections.subscriptions.premium.lifetime.button.label',
                    )}
                    {lifetimePriceLoading && (
                      <Styled.ButtonLoader color="primary" size={12} />
                    )}
                  </Button>
                </Styled.SubscriptionHeader>

                <Styled.SubscriptionDescription>
                  {t(
                    'forms.user-settings.sections.subscriptions.premium.lifetime.description',
                  )}
                </Styled.SubscriptionDescription>
              </Styled.SubscriptionContainer>
            </Styled.SplitSectionRow>
          )}

          {!!promotionalSubscription && (
            <Styled.SplitSectionRow>
              <Styled.SubscriptionContainer>
                <Styled.SubscriptionHeader>
                  <Styled.SubscriptionHeaderContent>
                    <Styled.SubscriptionIcon>
                      <Icon icon={Logo} />
                    </Styled.SubscriptionIcon>

                    <Styled.SubscriptionHeaderTitle>
                      {t(
                        'forms.user-settings.sections.subscriptions.premium.promotional.label',
                      )}

                      <Styled.Pill $success $warning={false} $error={false}>
                        {t(
                          'forms.user-settings.sections.subscriptions.premium.promotional.pill.active',
                        )}
                      </Styled.Pill>
                    </Styled.SubscriptionHeaderTitle>
                  </Styled.SubscriptionHeaderContent>
                </Styled.SubscriptionHeader>

                {!!promotionalSubscription.expiresAt && (
                  <Styled.SubscriptionDescription>
                    {t(
                      'forms.user-settings.sections.subscriptions.premium.promotional.description',
                      {
                        date: format(
                          promotionalSubscription.expiresAt,
                          'd MMM yyyy',
                        ),
                      },
                    )}
                  </Styled.SubscriptionDescription>
                )}
              </Styled.SubscriptionContainer>
            </Styled.SplitSectionRow>
          )}
        </Styled.Section>
      )}
    </>
  );
};
